import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Printer } from "lucide-react";
import { obtenerCartera } from "../services/movimientoService";
import { obtenerXMLFactura } from "../services/facturaService";
import PageHeader from "../components/PageHeader";
import Toast, { useToast } from "../components/Toast";
import { getLogoUrl, getEmpresaId } from "../utils/logoUtils";

const formatDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("es-MX", { year: "numeric", month: "2-digit", day: "2-digit" }); }
  catch { return String(d).slice(0, 10); }
};
const formatCurrency = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const scrTh = { padding: "8px 12px", textAlign: "center", fontWeight: "var(--fw-semibold)", fontSize: "var(--fs-sm)", whiteSpace: "nowrap" };
const scrTd = { padding: "7px 12px", textAlign: "center", verticalAlign: "middle" };

// Muestra un monto separado por moneda: solo pinta la moneda que tenga saldo
const MontoPorMoneda = ({ mxn, usd, color }) => (
  <>
    {(mxn !== 0 || usd === 0) && (
      <div style={{ whiteSpace: "nowrap", color }}>{formatCurrency(mxn)} MXN</div>
    )}
    {usd !== 0 && (
      <div style={{ whiteSpace: "nowrap", color }}>{formatCurrency(usd)} USD</div>
    )}
  </>
);

// ── Vista de impresión (mismo formato que el Reporte de Pagos) ───────────────
const pth = { padding: "4px 6px", fontWeight: "bold", textAlign: "center" };
const ptd = { padding: "2px 6px" };

function PrintView({ grupos }) {
  const hoy = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });

  const totGlobal = grupos.reduce(
    (acc, g) => {
      for (const m of g.rows) {
        const importe = Number(m.importeFactura) || 0;
        const saldo = Number(m.saldoFactura) || 0;
        if (m.moneda === "D") { acc.importeUsd += importe; acc.saldoUsd += saldo; }
        else { acc.importeMxn += importe; acc.saldoMxn += saldo; }
      }
      return acc;
    },
    { importeMxn: 0, importeUsd: 0, saldoMxn: 0, saldoUsd: 0 }
  );

  return (
    <div style={{ padding: "16px 20px", fontFamily: "Arial, sans-serif", fontSize: 10, color: "#000" }}>
      {/* Encabezado */}
      <table style={{ width: "100%", marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: "15%", verticalAlign: "middle" }}>
              <img src={getLogoUrl(getEmpresaId())} alt="Logo" style={{ height: 52, objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
            </td>
            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
              <div style={{ fontSize: 15, fontWeight: "bold" }}>LMD INTEGRADORA DE</div>
              <div style={{ fontSize: 13, fontWeight: "bold", marginTop: 4 }}>Cartera de Clientes</div>
              <div style={{ fontSize: 10, marginTop: 4 }}>Reporte al: {hoy}</div>
            </td>
            <td style={{ width: "10%", textAlign: "right", verticalAlign: "top", fontSize: 9 }}>1 De 1</td>
          </tr>
        </tbody>
      </table>

      {/* Tabla */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #333" }}>
            <th style={{ ...pth, width: "8%",  textAlign: "left"  }}>CTE.</th>
            <th style={{ ...pth, width: "26%", textAlign: "left"  }}>Nombre</th>
            <th style={{ ...pth, width: "10%" }}>Documento</th>
            <th style={{ ...pth, width: "10%" }}>Fecha</th>
            <th style={{ ...pth, width: "10%" }}>Vencimiento</th>
            <th style={{ ...pth, width: "6%",  textAlign: "right" }}>Dias</th>
            <th style={{ ...pth, width: "12%", textAlign: "right" }}>Importe</th>
            <th style={{ ...pth, width: "12%", textAlign: "right" }}>Saldo</th>
            <th style={{ ...pth, width: "6%"  }}>Mon.</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map((g) => {
            const totCte = g.rows.reduce(
              (acc, m) => {
                const importe = Number(m.importeFactura) || 0;
                const saldo = Number(m.saldoFactura) || 0;
                if (m.moneda === "D") { acc.importeUsd += importe; acc.saldoUsd += saldo; }
                else { acc.importeMxn += importe; acc.saldoMxn += saldo; }
                return acc;
              },
              { importeMxn: 0, importeUsd: 0, saldoMxn: 0, saldoUsd: 0 }
            );
            return (
              <React.Fragment key={g.clave}>
                {/* Fila de cliente */}
                <tr>
                  <td style={{ ...ptd, fontWeight: "bold", paddingTop: 8 }}>{g.clave}</td>
                  <td colSpan={8} style={{ ...ptd, fontWeight: "bold", paddingTop: 8 }}>{g.nombre}</td>
                </tr>
                {/* Documentos */}
                {g.rows.map((m, i) => {
                  const dias = Number(m.diasCartera) || 0;
                  return (
                    <tr key={i}>
                      <td style={ptd} />
                      <td style={ptd} />
                      <td style={{ ...ptd, textAlign: "center" }}>{m.serie}-{m.numeroDocumento}</td>
                      <td style={{ ...ptd, textAlign: "center" }}>{formatDate(m.fechaMovimiento)}</td>
                      <td style={{ ...ptd, textAlign: "center" }}>{formatDate(m.fechaVencimiento)}</td>
                      <td style={{ ...ptd, textAlign: "right" }}>{dias > 0 && m.saldoFactura > 0 ? dias : ""}</td>
                      <td style={{ ...ptd, textAlign: "right" }}>{formatCurrency(m.importeFactura)}</td>
                      <td style={{ ...ptd, textAlign: "right" }}>{formatCurrency(m.saldoFactura)}</td>
                      <td style={{ ...ptd, textAlign: "center" }}>{m.moneda === "D" ? "USD" : "MXN"}</td>
                    </tr>
                  );
                })}
                {/* Total por cliente */}
                <tr style={{ borderTop: "1px solid #ccc" }}>
                  <td colSpan={6} style={{ ...ptd, textAlign: "right", fontWeight: "bold" }}>TOTAL</td>
                  <td style={{ ...ptd, textAlign: "right", fontWeight: "bold" }}>
                    <MontoPorMoneda mxn={totCte.importeMxn} usd={totCte.importeUsd} />
                  </td>
                  <td style={{ ...ptd, textAlign: "right", fontWeight: "bold" }}>
                    <MontoPorMoneda mxn={totCte.saldoMxn} usd={totCte.saldoUsd} />
                  </td>
                  <td style={ptd} />
                </tr>
              </React.Fragment>
            );
          })}

          {/* Total global */}
          <tr style={{ borderTop: "2px solid #333" }}>
            <td colSpan={6} style={{ ...ptd, textAlign: "right", fontWeight: "bold", paddingTop: 6 }}>TOTAL</td>
            <td style={{ ...ptd, textAlign: "right", fontWeight: "bold", paddingTop: 6 }}>
              <MontoPorMoneda mxn={totGlobal.importeMxn} usd={totGlobal.importeUsd} />
            </td>
            <td style={{ ...ptd, textAlign: "right", fontWeight: "bold", paddingTop: 6 }}>
              <MontoPorMoneda mxn={totGlobal.saldoMxn} usd={totGlobal.saldoUsd} />
            </td>
            <td style={ptd} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function CarteraPage() {
  const location = useLocation();
  const [cartera, setCartera] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ clienteClave: "", moneda: "TODAS", tipoCartera: "TODAS", diasAtraso: 0 });
  const [downloadingKey, setDownloadingKey] = useState(null);
  const empresaId = localStorage.getItem("empresaId") || 1;
  const { toasts, closeToast, error: toastError } = useToast();

  const handleDownloadXML = useCallback(async (r) => {
    const key = `${r.serie}-${r.numeroDocumento}`;
    setDownloadingKey(key);
    try {
      const xml = await obtenerXMLFactura({ serie: r.serie, factura: r.numeroDocumento, empresa: empresaId });
      const blob = new Blob([xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toastError(`No se encontró el XML para la factura ${key}.`);
    } finally {
      setDownloadingKey(null);
    }
  }, [empresaId]);

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: name === "diasAtraso" ? Number(value) : value }));
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const empresaId = Number(localStorage.getItem("empresaId") || 1);
        const data = await obtenerCartera(empresaId);
        if (!cancel) { setCartera(data || []); setError(null); }
      } catch (e) {
        if (!cancel) { setError("Error al cargar la cartera."); setCartera([]); }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const filteredCartera = useMemo(() => {
    let list = cartera;
    const { clienteClave, moneda, diasAtraso } = filters;
    if (moneda !== "TODAS") list = list.filter((m) => m.moneda === moneda);
    if (clienteClave) {
      const term = clienteClave.toLowerCase();
      list = list.filter((m) => (m.claveCliente || "").toLowerCase().includes(term) || (m.nombreCliente || "").toLowerCase().includes(term));
    }
    if (diasAtraso > 0) list = list.filter((m) => (Number(m.diasCartera) || 0) >= diasAtraso);
    return list;
  }, [cartera, filters]);

  // Totales de saldo separados por moneda
  const totalesCartera = useMemo(() =>
    filteredCartera.reduce(
      (acc, m) => {
        const saldo = Number(m.saldoFactura) || 0;
        if (m.moneda === "D") acc.usd += saldo; else acc.mxn += saldo;
        return acc;
      },
      { mxn: 0, usd: 0 }
    ),
  [filteredCartera]);

  // Agrupar documentos por cliente (mismo formato que el Reporte de Pagos)
  const grupos = useMemo(() => {
    const map = new Map();
    for (const m of filteredCartera) {
      const clave = m.claveCliente || "SIN CLAVE";
      const claveKey = clave.toUpperCase();
      if (!map.has(claveKey)) {
        map.set(claveKey, { clave: clave.toUpperCase(), nombre: m.nombreCliente || "N/A", rows: [] });
      }
      map.get(claveKey).rows.push(m);
    }
    return Array.from(map.values());
  }, [filteredCartera]);

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <div className="reporte-no-print">
        <PageHeader title="Cartera de Clientes" subtitle="Seguimiento de documentos pendientes de cobro" />
      </div>

      {/* Filtros */}
      <div className="lmd-card reporte-no-print" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Filtros</h3>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <span style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>
              {filteredCartera.length} documentos
            </span>
            <span style={{ fontWeight: "var(--fw-bold)", color: (totalesCartera.mxn > 0 || totalesCartera.usd > 0) ? "var(--clr-danger)" : "var(--clr-text-primary)", fontSize: "var(--fs-md)", textAlign: "right" }}>
              Total: {formatCurrency(totalesCartera.mxn)} MXN
              {totalesCartera.usd !== 0 && ` + ${formatCurrency(totalesCartera.usd)} USD`}
            </span>
            <button
              className="lmd-btn lmd-btn-secondary"
              onClick={() => window.print()}
              disabled={loading || filteredCartera.length === 0}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Printer size={15} /> Imprimir
            </button>
          </div>
        </div>
        <div className="lmd-card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--sp-4)" }}>
            <div className="lmd-field">
              <label className="lmd-label">Cliente</label>
              <input className="lmd-input" name="clienteClave" value={filters.clienteClave} onChange={onChangeFilter} placeholder="Clave o nombre..." />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Moneda</label>
              <select className="lmd-select" name="moneda" value={filters.moneda} onChange={onChangeFilter} style={{ width: "100%" }}>
                <option value="TODAS">Todas</option>
                <option value="P">MXN</option>
                <option value="D">USD</option>
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Tipo Cartera</label>
              <select className="lmd-select" name="tipoCartera" value={filters.tipoCartera} onChange={onChangeFilter} style={{ width: "100%" }}>
                <option value="TODAS">Todas</option>
                <option value="1">Normal</option>
                <option value="2">Incobrable</option>
                <option value="3">Empleados</option>
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Dias vencidos ≥</label>
              <input className="lmd-input" type="number" name="diasAtraso" value={filters.diasAtraso} onChange={onChangeFilter} min="0" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="lmd-card reporte-no-print">
        {loading && <div className="lmd-loading-bar"><div className="lmd-spinner" />Cargando cartera...</div>}
        {error && <div style={{ padding: "16px 20px", color: "var(--clr-danger)", background: "var(--clr-danger-bg)" }}>{error}</div>}
        {!loading && !error && (
          filteredCartera.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--clr-text-muted)" }}>
              No hay documentos pendientes con los filtros seleccionados.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-sm)" }}>
                <thead>
                  <tr style={{ background: "var(--clr-table-head-bg, #f3f4f6)", borderBottom: "2px solid var(--clr-border)" }}>
                    <th style={{ ...scrTh, textAlign: "left", width: 170 }}>Documento</th>
                    <th style={{ ...scrTh, width: 110 }}>Fecha</th>
                    <th style={{ ...scrTh, width: 110 }}>Vencimiento</th>
                    <th style={{ ...scrTh, textAlign: "right", width: 80 }}>Dias</th>
                    <th style={{ ...scrTh, textAlign: "right", width: 130 }}>Importe</th>
                    <th style={{ ...scrTh, textAlign: "right", width: 130 }}>Saldo</th>
                    <th style={{ ...scrTh, width: 90 }}>Moneda</th>
                  </tr>
                </thead>
                <tbody>
                  {grupos.map((g) => {
                    const totCte = g.rows.reduce(
                      (acc, m) => {
                        const importe = Number(m.importeFactura) || 0;
                        const saldo = Number(m.saldoFactura) || 0;
                        if (m.moneda === "D") {
                          acc.importeUsd += importe;
                          acc.saldoUsd += saldo;
                        } else {
                          acc.importeMxn += importe;
                          acc.saldoMxn += saldo;
                        }
                        return acc;
                      },
                      { importeMxn: 0, importeUsd: 0, saldoMxn: 0, saldoUsd: 0 }
                    );
                    return (
                      <React.Fragment key={g.clave}>
                        {/* Fila del cliente */}
                        <tr style={{ background: "var(--clr-primary-subtle, #EFF6FF)" }}>
                          <td colSpan={7} style={{ padding: "6px 12px", fontWeight: "var(--fw-bold)", color: "var(--clr-primary)" }}>
                            {g.clave} &mdash; {g.nombre}
                          </td>
                        </tr>
                        {/* Documentos del cliente */}
                        {g.rows.map((r, i) => {
                          const key = `${r.serie}-${r.numeroDocumento}`;
                          const isDownloading = downloadingKey === key;
                          const dias = Number(r.diasCartera) || 0;
                          const overdue = dias > 0 && r.saldoFactura > 0;
                          return (
                            <tr key={key} style={{ borderBottom: "1px solid var(--clr-border)", background: i % 2 === 0 ? "#fff" : "var(--clr-table-stripe, #f9fafb)" }}>
                              <td style={{ ...scrTd, textAlign: "left" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <Link
                                    to={`/factura/detalle/${r.serie}/${r.numeroDocumento}/${empresaId}`}
                                    state={{ background: location }}
                                    style={{ color: "var(--clr-primary)", fontWeight: "var(--fw-semibold)", textDecoration: "none" }}
                                  >
                                    {key}
                                  </Link>
                                  <button
                                    onClick={() => handleDownloadXML(r)}
                                    disabled={isDownloading}
                                    title="Descargar XML"
                                    style={{
                                      background: "none", border: "none", cursor: isDownloading ? "wait" : "pointer",
                                      padding: "2px 4px", color: isDownloading ? "var(--clr-text-muted)" : "var(--clr-primary)",
                                      lineHeight: 1, fontSize: 14, display: "flex", alignItems: "center",
                                    }}
                                  >
                                    {isDownloading ? "⏳" : "⬇️"}
                                  </button>
                                </div>
                              </td>
                              <td style={scrTd}>{formatDate(r.fechaMovimiento)}</td>
                              <td style={scrTd}>{formatDate(r.fechaVencimiento)}</td>
                              <td style={{ ...scrTd, textAlign: "right" }}>
                                {overdue
                                  ? <span style={{ color: "var(--clr-danger)", fontWeight: "var(--fw-semibold)" }}>{dias}</span>
                                  : <span style={{ color: "var(--clr-text-muted)" }}>—</span>}
                              </td>
                              <td style={{ ...scrTd, textAlign: "right" }}>{formatCurrency(r.importeFactura)}</td>
                              <td style={{ ...scrTd, textAlign: "right" }}>
                                <strong style={{ color: overdue ? "var(--clr-danger)" : "var(--clr-text-primary)" }}>
                                  {formatCurrency(r.saldoFactura)}
                                </strong>
                              </td>
                              <td style={scrTd}>
                                <span className={`lmd-badge ${r.moneda === "D" ? "lmd-badge-primary" : "lmd-badge-neutral"}`}>
                                  {r.moneda === "D" ? "USD" : "MXN"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Total por cliente */}
                        <tr style={{ borderTop: "1px solid var(--clr-border)", background: "var(--clr-table-head-bg, #f3f4f6)" }}>
                          <td colSpan={4} style={{ ...scrTd, textAlign: "right", fontWeight: "var(--fw-bold)" }}>TOTAL</td>
                          <td style={{ ...scrTd, textAlign: "right", fontWeight: "var(--fw-bold)" }}>
                            <MontoPorMoneda mxn={totCte.importeMxn} usd={totCte.importeUsd} />
                          </td>
                          <td style={{ ...scrTd, textAlign: "right", fontWeight: "var(--fw-bold)" }}>
                            <MontoPorMoneda mxn={totCte.saldoMxn} usd={totCte.saldoUsd} />
                          </td>
                          <td style={scrTd} />
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {/* Total global */}
              <div style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "12px 20px",
                borderTop: "2px solid var(--clr-border)",
                fontWeight: "var(--fw-bold)",
                fontSize: "var(--fs-md)",
              }}>
                <span style={{ color: (totalesCartera.mxn > 0 || totalesCartera.usd > 0) ? "var(--clr-danger)" : "var(--clr-text-primary)", textAlign: "right" }}>
                  Total cartera: {formatCurrency(totalesCartera.mxn)} MXN
                  {totalesCartera.usd !== 0 && ` + ${formatCurrency(totalesCartera.usd)} USD`}
                </span>
              </div>
            </div>
          )
        )}
      </div>

      {/* Vista de impresión */}
      <div className="reporte-solo-print">
        <PrintView grupos={grupos} />
      </div>

      <style>{`
        @media screen { .reporte-solo-print { display: none; } }
        @media print  {
          .reporte-no-print { display: none !important; }
          .reporte-solo-print { display: block !important; }
        }
      `}</style>
    </>
  );
}
